import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Box,
  CircularProgress,
  makeStyles,
  Tooltip
} from '@material-ui/core';
import {
  Edit as EditIcon,
  Visibility as ViewIcon,
  Assignment as AssignIcon,
  TrendingUp as EscalateIcon,
  CheckCircle as ResolveIcon,
  FilterList as FilterIcon,
  GetApp as ExportIcon,
  Add as AddIcon
} from '@material-ui/icons';
import moment from 'moment';
import CustomerComplaintModal from './CustomerComplaintModal';
import CustomerComplaintDetailModal from './CustomerComplaintDetailModal';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  filters: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: '#f5f5f5',
  },
  table: {
    minWidth: 650,
  },
  priorityChip: {
    fontWeight: 'bold',
  },
  statusChip: {
    fontWeight: 'bold',
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  statsCard: {
    padding: theme.spacing(2),
    textAlign: 'center',
    height: '100%',
  },
  statNumber: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  tabPanel: {
    paddingTop: theme.spacing(2),
  },
}));

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box>{children}</Box>}
  </div>
);

const CustomerComplaintDashboard = ({ url, user, entity }) => {
  const classes = useStyles();
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [tabValue, setTabValue] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedComplaintId, setSelectedComplaintId] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [stats, setStats] = useState({
    open: 0,
    inProgress: 0,
    resolved: 0,
    escalated: 0,
    overdue: 0,
  });
  
  // Filters
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    complaintType: '',
    category: '',
    searchTerm: '',
    dateFrom: '',
    dateTo: '',
  });

  const priorityColors = {
    low: '#4caf50',
    normal: '#ff9800',
    medium: '#ff9800',
    high: '#f44336',
    urgent: '#d32f2f',
    critical: '#b71c1c',
  };

  const categoryLabels = {
    landscape_quality: 'Landscape Quality',
    quick_ticket: 'Quick Ticket',
    crew_feedback: 'Crew Feedback',
    service_change: 'Service Change',
    other: 'Other',
  };

  const categoryColors = {
    landscape_quality: '#43a047',
    quick_ticket: '#1e88e5',
    crew_feedback: '#8e24aa',
    service_change: '#f4511e',
    other: '#757575',
  };

  const statusColors = {
    open: '#2196f3',
    in_progress: '#ff9800',
    resolved: '#4caf50',
    closed: '#9e9e9e',
    escalated: '#f44336',
  };

  useEffect(() => {
    fetchComplaints();
    fetchStats();
  }, [page, rowsPerPage, filters, tabValue]);

  const fetchComplaints = async () => {
    try {
      setLoading(true);
      
      let statusFilter = '';
      if (tabValue === 1) statusFilter = 'open';
      else if (tabValue === 2) statusFilter = 'in_progress';
      else if (tabValue === 3) statusFilter = 'resolved';
      else if (tabValue === 4) statusFilter = 'escalated';
      
      const params = {
        entityId: entity?._id,
        page: page + 1,
        limit: rowsPerPage,
        status: statusFilter || filters.status,
        priority: filters.priority,
        complaintType: filters.complaintType,
        category: filters.category,
        startDate: filters.dateFrom,
        endDate: filters.dateTo,
      };

      const response = await axios.get(`${url}/api/customer/complaints`, { params });
      
      if (response.data.success) {
        setComplaints(response.data.complaints);
        setTotalCount(response.data.total);
      }
    } catch (error) {
      console.error('Error fetching complaints:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${url}/api/customer/complaints/stats/${entity?._id}`);
      if (response.data.success) {
        const statusBreakdown = response.data.stats.statusBreakdown;
        const newStats = {
          open: 0,
          inProgress: 0,
          resolved: 0,
          escalated: 0,
          overdue: response.data.stats.overdueCount || 0,
        };
        
        statusBreakdown.forEach(item => {
          if (item.status === 'open') newStats.open = item.count;
          else if (item.status === 'in_progress') newStats.inProgress = item.count;
          else if (item.status === 'resolved') newStats.resolved = item.count;
          else if (item.status === 'escalated') newStats.escalated = item.count;
        });
        
        setStats(newStats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleUpdateComplaint = async (complaintId, updates) => {
    try {
      const response = await axios.put(
        `${url}/api/customer/complaints/${complaintId}`,
        updates
      );
      
      if (response.data.success) {
        fetchComplaints();
        fetchStats();
        return true;
      }
    } catch (error) {
      console.error('Error updating complaint:', error);
      return false;
    }
  };

  const handleEscalate = async (complaint) => {
    const reason = prompt('Please provide a reason for escalation:');
    if (reason) {
      try {
        const response = await axios.post(
          `${url}/api/customer/complaints/${complaint._id}/escalate`,
          { reason }
        );
        
        if (response.data.success) {
          fetchComplaints();
          fetchStats();
        }
      } catch (error) {
        console.error('Error escalating complaint:', error);
      }
    }
  };

  const handleViewDetails = (complaint) => {
    setSelectedComplaintId(complaint._id);
    setShowDetailModal(true);
  };

  const handleExport = async () => {
    try {
      const params = {
        entityId: entity?._id,
        ...filters,
      };
      
      const response = await axios.get(`${url}/api/customer/complaints`, { 
        params,
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `complaints_${moment().format('YYYY-MM-DD')}.csv`;
      link.click();
    } catch (error) {
      console.error('Error exporting complaints:', error);
    }
  };

  const renderComplaintRow = (complaint) => {
    const typeLabel = complaint.complaintType || categoryLabels[complaint.category] || '—';
    const subcatLabel = complaint.subcategory
      ? complaint.subcategory.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
      : null;

    return (
    <TableRow key={complaint._id}>
      <TableCell>{moment(complaint.dateReported).format('MM/DD/YYYY')}</TableCell>
      <TableCell>{complaint.clientName || complaint.jobsiteName || '—'}</TableCell>
      <TableCell>
        <div>
          <span>{typeLabel}</span>
          {complaint.category && (
            <Chip
              label={categoryLabels[complaint.category] || complaint.category}
              size="small"
              style={{
                backgroundColor: categoryColors[complaint.category] || '#757575',
                color: 'white',
                marginLeft: 6,
                height: 20,
                fontSize: '0.7rem',
              }}
            />
          )}
          {subcatLabel && (
            <Typography variant="caption" display="block" color="textSecondary">
              {subcatLabel}
            </Typography>
          )}
        </div>
      </TableCell>
      <TableCell>
        <Chip
          label={complaint.priority}
          size="small"
          className={classes.priorityChip}
          style={{
            backgroundColor: priorityColors[complaint.priority] || '#9e9e9e',
            color: 'white'
          }}
        />
      </TableCell>
      <TableCell>
        <Chip
          label={complaint.status.replace('_', ' ')}
          size="small"
          className={classes.statusChip}
          style={{
            backgroundColor: statusColors[complaint.status],
            color: 'white'
          }}
        />
      </TableCell>
      <TableCell>{complaint.contactName || '—'}</TableCell>
      <TableCell>{complaint.assignedTo?.displayName || complaint.assignedEmployee?.displayName || 'Unassigned'}</TableCell>
      <TableCell>
        <div className={classes.actionButtons}>
          <Tooltip title="View Details">
            <IconButton size="small" onClick={() => handleViewDetails(complaint)}>
              <ViewIcon />
            </IconButton>
          </Tooltip>
          
          {complaint.status === 'open' && (
            <Tooltip title="Assign">
              <IconButton 
                size="small" 
                onClick={() => {
                  const assignee = prompt('Enter assignee user ID:');
                  if (assignee) {
                    handleUpdateComplaint(complaint._id, { 
                      assignedTo: assignee,
                      status: 'in_progress' 
                    });
                  }
                }}
              >
                <AssignIcon />
              </IconButton>
            </Tooltip>
          )}
          
          {complaint.status !== 'resolved' && complaint.status !== 'closed' && (
            <Tooltip title="Resolve">
              <IconButton 
                size="small"
                onClick={() => {
                  const resolution = prompt('Enter resolution:');
                  if (resolution) {
                    handleUpdateComplaint(complaint._id, { 
                      status: 'resolved',
                      resolution 
                    });
                  }
                }}
              >
                <ResolveIcon />
              </IconButton>
            </Tooltip>
          )}
          
          {complaint.status !== 'escalated' && (
            <Tooltip title="Escalate">
              <IconButton 
                size="small"
                onClick={() => handleEscalate(complaint)}
              >
                <EscalateIcon />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </TableCell>
    </TableRow>
    );
  };

  return (
    <div className={classes.root}>
      {/* Header */}
      <Grid container spacing={3} className={classes.header}>
        <Grid item xs={12} md={6}>
          <Typography variant="h4">Customer Complaints Dashboard</Typography>
        </Grid>
        <Grid item xs={12} md={6} style={{ textAlign: 'right' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setShowCreateModal(true)}
            style={{ marginRight: 10 }}
          >
            New Complaint
          </Button>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={handleExport}
          >
            Export
          </Button>
        </Grid>
      </Grid>

      {/* Statistics Cards */}
      <Grid container spacing={3} style={{ marginBottom: 20 }}>
        <Grid item xs={6} md={2}>
          <Paper className={classes.statsCard}>
            <Typography variant="subtitle2">Open</Typography>
            <Typography className={classes.statNumber}>{stats.open}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={2}>
          <Paper className={classes.statsCard}>
            <Typography variant="subtitle2">In Progress</Typography>
            <Typography className={classes.statNumber}>{stats.inProgress}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={2}>
          <Paper className={classes.statsCard}>
            <Typography variant="subtitle2">Resolved</Typography>
            <Typography className={classes.statNumber} style={{ color: '#4caf50' }}>
              {stats.resolved}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={2}>
          <Paper className={classes.statsCard}>
            <Typography variant="subtitle2">Escalated</Typography>
            <Typography className={classes.statNumber} style={{ color: '#f44336' }}>
              {stats.escalated}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={2}>
          <Paper className={classes.statsCard}>
            <Typography variant="subtitle2">Overdue</Typography>
            <Typography className={classes.statNumber} style={{ color: '#ff9800' }}>
              {stats.overdue}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper className={classes.filters}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Priority</InputLabel>
              <Select
                value={filters.priority}
                onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
                label="Priority"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={filters.complaintType}
                onChange={(e) => setFilters({ ...filters, complaintType: e.target.value })}
                label="Type"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Service Quality">Service Quality</MenuItem>
                <MenuItem value="Scheduling Issue">Scheduling Issue</MenuItem>
                <MenuItem value="Billing/Invoice">Billing/Invoice</MenuItem>
                <MenuItem value="Property Damage">Property Damage</MenuItem>
                <MenuItem value="Communication">Communication</MenuItem>
                <MenuItem value="Staff Behavior">Staff Behavior</MenuItem>
                <MenuItem value="Safety Concern">Safety Concern</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={filters.category}
                onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                label="Category"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="landscape_quality">Landscape Quality</MenuItem>
                <MenuItem value="quick_ticket">Quick Ticket</MenuItem>
                <MenuItem value="crew_feedback">Crew Feedback</MenuItem>
                <MenuItem value="service_change">Service Change</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label="Date From"
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label="Date To"
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Paper style={{ marginTop: 20 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="All" />
          <Tab label="Open" />
          <Tab label="In Progress" />
          <Tab label="Resolved" />
          <Tab label="Escalated" />
        </Tabs>

        {/* Table */}
        <TableContainer>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 50 }}>
              <CircularProgress />
            </div>
          ) : (
            <Table className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Type / Category</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Assigned To</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {complaints.map(renderComplaintRow)}
              </TableBody>
            </Table>
          )}
          
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onChangePage={(e, newPage) => setPage(newPage)}
            onChangeRowsPerPage={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </TableContainer>
      </Paper>

      {/* Create Modal */}
      <CustomerComplaintModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onComplaintCreated={() => {
          setShowCreateModal(false);
          fetchComplaints();
          fetchStats();
        }}
      />

      {/* Detail Modal */}
      <CustomerComplaintDetailModal
        open={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedComplaintId(null);
        }}
        complaintId={selectedComplaintId}
        onUpdate={() => {
          fetchComplaints();
          fetchStats();
        }}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user,
  entity: state.entity,
});

export default connect(mapStateToProps)(CustomerComplaintDashboard);